<template>
  <div class="splash-overlay" @click="handleOverlayClick">
    <div class="splash-modal" @click.stop>
      <!-- Close button -->
      <button @click="closeSplash" class="close-btn" aria-label="Close">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <!-- Header -->
      <div class="splash-header">
        <div class="icon-container">
          <div class="floating-icons">
            <div class="floating-icon" style="animation-delay: 0s;">📚</div>
            <div class="floating-icon" style="animation-delay: 0.5s;">🎯</div>
            <div class="floating-icon" style="animation-delay: 1s;">⭐</div>
          </div>
        </div>
        <h2 class="splash-title">Welcome to Your Personalized Learning!</h2>
        <p class="splash-subtitle">Your questions are tailored to your state's educational standards</p>
      </div>
      
      <!-- Content -->
      <div class="splash-content">
        <!-- State-specific content -->
        <div v-if="stateInfo.hasCustomStandards" class="state-specific-section">
          <div class="state-badge">
            <span class="state-icon">🏛️</span>
            <span class="state-name">{{ stateInfo.name }}</span>
          </div>
          
          <div class="standards-info">
            <h3 class="section-title">Your State's Standards</h3>
            <p class="standards-text">
              Great news! We have questions specifically aligned with 
              <strong>{{ stateInfo.standards }}</strong> used in {{ stateInfo.name }}.
            </p>
            
            <div class="benefits-list">
              <div class="benefit-item">
                <span class="benefit-icon">✅</span>
                <span>Questions match your state's curriculum</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🎯</span>
                <span>Aligned with what you're learning in school</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">📈</span>
                <span>Better preparation for state assessments</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- CCSS content -->
        <div v-else class="ccss-section">
          <div class="ccss-badge">
            <span class="ccss-icon">📋</span>
            <span class="ccss-name">Common Core Standards</span>
          </div>
          
          <div class="standards-info">
            <h3 class="section-title">Your Learning Standards</h3>
            <p class="standards-text">
              Your questions are aligned with the <strong>Common Core State Standards (CCSS)</strong>
              {{ stateInfo.name ? `used in ${stateInfo.name}` : '' }}.
            </p>
            
            <div class="benefits-list">
              <div class="benefit-item">
                <span class="benefit-icon">📚</span>
                <span>Nationally recognized standards</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🌟</span>
                <span>College and career readiness focus</span>
              </div>
              <div class="benefit-item">
                <span class="benefit-icon">🤝</span>
                <span>Consistent with most US schools</span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Subject breakdown -->
        <div class="subjects-section">
          <h3 class="section-title">Available Subjects</h3>
          <div class="subjects-grid">
            <div class="subject-card">
              <span class="subject-icon">🔢</span>
              <span class="subject-name">Mathematics</span>
              <span class="subject-desc">Algebra, Geometry, Statistics</span>
            </div>
            <div class="subject-card">
              <span class="subject-icon">📖</span>
              <span class="subject-name">English</span>
              <span class="subject-desc">Reading, Writing, Grammar</span>
            </div>
            <div class="subject-card">
              <span class="subject-icon">🔬</span>
              <span class="subject-name">Science</span>
              <span class="subject-desc">Biology, Chemistry, Physics</span>
            </div>
            <div class="subject-card">
              <span class="subject-icon">🏛️</span>
              <span class="subject-name">History</span>
              <span class="subject-desc">US & World History, Civics</span>
            </div>
          </div>
        </div>
        
        <!-- Additional info -->
        <div class="additional-info">
          <div class="info-card">
            <div class="info-icon">🔄</div>
            <div class="info-content">
              <h4>Smart Fallback System</h4>
              <p>If specific questions aren't available, we'll provide the best alternative content to keep you learning.</p>
            </div>
          </div>
          
          <div class="info-card">
            <div class="info-icon">📊</div>
            <div class="info-content">
              <h4>Adaptive Learning</h4>
              <p>Questions adjust to your skill level, providing the right challenge for optimal learning.</p>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Footer -->
      <div class="splash-footer">
        <div class="footer-buttons">
          <button @click="closeSplash" class="btn-secondary">
            Got It!
          </button>
          <button @click="startLearning" class="btn-primary">
            Start Learning
          </button>
        </div>
        <div class="footer-note">
          <span class="note-icon">💡</span>
          <span>You can always update your state in profile settings</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
// Inline state question utilities (formerly from ../utils/stateQuestions.js)
function isStateUsingCCSS(state) {
  // States with state-specific collections: alabama, florida, indiana, missouri, oklahoma, south carolina, texas, west virginia
  const stateSpecificStates = [
    'alabama', 'florida', 'indiana', 'missouri', 'oklahoma', 
    'south carolina', 'texas', 'west virginia',
    // Add state abbreviations
    'al', 'fl', 'in', 'mo', 'ok', 'sc', 'tx', 'wv'
  ];
  
  // If state is in the state-specific list, it does NOT use CCSS (returns false)
  // All other states use CCSS and prod_questions collection
  return !stateSpecificStates.includes(state?.toLowerCase());
}

function getStateInfo(state) {
  const stateName = state || 'Unknown';
  const usesCCSS = isStateUsingCCSS(state);
  
  return {
    name: stateName,
    usesCCSS: usesCCSS,
    customStandards: !usesCCSS,
    standards: usesCCSS ? 'Common Core State Standards (CCSS)' : `${stateName} State Standards`
  };
}

const props = defineProps({
  userState: {
    type: String,
    default: null
  },
  userProfile: {
    type: Object,
    default: () => ({})
  }
});

const emit = defineEmits(['close', 'start-learning']);

// State information
const stateInfo = computed(() => {
  const info = getStateInfo(props.userState);
  return {
    ...info,
    hasCustomStandards: !isStateUsingCCSS(props.userState)
  };
});

// Methods
function closeSplash() {
  emit('close');
}

function startLearning() {
  emit('start-learning');
}

function handleOverlayClick() {
  // Allow closing by clicking outside modal
  closeSplash();
}

// Log state info for debugging
onMounted(() => {
  console.log('🎓 Question Source Splash - State Info:', stateInfo.value);
});
</script>

<style scoped>
.splash-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
  padding: 1rem;
}

.splash-modal {
  background: linear-gradient(145deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.25),
    0 12px 24px rgba(0, 0, 0, 0.15);
  animation: modalSlideIn 0.4s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  color: #64748b;
}

.close-btn:hover {
  background: rgba(0, 0, 0, 0.15);
  color: #374151;
  transform: scale(1.05);
}

.splash-header {
  text-align: center;
  padding: 2rem 2rem 1rem;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 24px 24px 0 0;
}

.icon-container {
  position: relative;
  height: 80px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.floating-icons {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2rem;
}

.floating-icon {
  font-size: 2rem;
  animation: floatSplash 3s ease-in-out infinite;
  opacity: 0.8;
}

@keyframes floatSplash {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.8;
  }
  33% {
    transform: translateY(-15px) rotate(5deg);
    opacity: 1;
  }
  66% {
    transform: translateY(5px) rotate(-3deg);
    opacity: 0.9;
  }
}

.splash-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.splash-subtitle {
  font-size: 1.1rem;
  opacity: 0.9;
  font-weight: 500;
}

.splash-content {
  padding: 2rem;
}

.state-specific-section,
.ccss-section {
  margin-bottom: 2rem;
}

.state-badge,
.ccss-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.ccss-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.state-icon,
.ccss-icon {
  font-size: 1.5rem;
}

.state-name,
.ccss-name {
  font-size: 1.25rem;
  font-weight: 600;
}

.standards-info {
  background: rgba(16, 185, 129, 0.05);
  border: 1px solid rgba(16, 185, 129, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
}

.ccss-section .standards-info {
  background: rgba(59, 130, 246, 0.05);
  border-color: rgba(59, 130, 246, 0.1);
}

.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1f2937;
}

.standards-text {
  font-size: 1rem;
  line-height: 1.6;
  color: #4b5563;
  margin-bottom: 1.5rem;
}

.benefits-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: #374151;
}

.benefit-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.subjects-section {
  margin-bottom: 2rem;
}

.subjects-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.subject-card {
  background: linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%);
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  transition: all 0.3s ease;
  cursor: default;
}

.subject-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
}

.subject-icon {
  display: block;
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
}

.subject-name {
  display: block;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.subject-desc {
  display: block;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.3;
}

.additional-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.info-card {
  display: flex;
  gap: 1rem;
  background: linear-gradient(145deg, #fefefe 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.25rem;
  align-items: flex-start;
}

.info-icon {
  font-size: 1.5rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  flex-shrink: 0;
}

.info-content h4 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #1f2937;
}

.info-content p {
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;
}

.splash-footer {
  padding: 2rem;
  border-top: 1px solid #e5e7eb;
  background: linear-gradient(145deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 0 0 24px 24px;
}

.footer-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 0.95rem;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: linear-gradient(145deg, #f1f5f9 0%, #e2e8f0 100%);
  color: #374151;
  border: 1px solid #d1d5db;
}

.btn-secondary:hover {
  background: linear-gradient(145deg, #e2e8f0 0%, #d1d5db 100%);
  transform: translateY(-1px);
}

.footer-note {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: #6b7280;
  font-style: italic;
}

.note-icon {
  font-size: 1rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .splash-modal {
    margin: 0.5rem;
    max-height: 95vh;
    border-radius: 16px;
  }
  
  .splash-header {
    padding: 1.5rem 1rem 1rem;
    border-radius: 16px 16px 0 0;
  }
  
  .splash-title {
    font-size: 1.5rem;
  }
  
  .splash-subtitle {
    font-size: 1rem;
  }
  
  .splash-content {
    padding: 1.5rem 1rem;
  }
  
  .splash-footer {
    padding: 1.5rem 1rem;
    border-radius: 0 0 16px 16px;
  }
  
  .footer-buttons {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .subjects-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .additional-info {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 480px) {
  .floating-icons {
    gap: 1rem;
  }
  
  .floating-icon {
    font-size: 1.5rem;
  }
  
  .splash-title {
    font-size: 1.25rem;
  }
  
  .subjects-grid {
    grid-template-columns: 1fr;
  }
}
</style>